import React, { useContext, useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { differenceInSeconds, parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import Button from '../../common/Button';
import CdnImageBackground from '../../common/CdnImageBackground';
import { H1, H4, H5 } from '../../text';
import LayoutContext from '../../../contexts/Layout';

const PERIOD_TYPE = Object.freeze({
    DAYS: 'Days',
    HOURS: 'Hrs.',
    MINUTES: 'Mins.',
    SECONDS: 'Secs.'
});

export const Countdown = ({ imageSrc, actionText, onPress, start, text, textColor, width, hardWidth = width }) => {
    const { layout } = useContext(LayoutContext);
    const [countdown, setCountdown] = useState();
    const [imageDimensions, setImageDimensions] = useState();

    const renderPeriod = (periodType, value) => {
        return (
            <View style={{ justifyContent: 'center', marginRight: layout.styleGuide.spacer9 }} dataSet={{ spacerRight: `StyleGuide.Spacer9(${layout.styleGuide.spacer9})` }}>
                <H1
                    style={StyleSheet.flatten(Styles.periodText, { color: textColor || '#fff', marginBottom: layout.styleGuide.spacer3 })}
                    text={value}
                    dataSet={{ spacerBottom: `StyleGuide.Spacer3(${layout.styleGuide.spacer3})` }}
                />
                <H5 style={StyleSheet.flatten(Styles.periodValue, { color: textColor || '#fff' })} text={periodType} />
            </View>
        );
    };

    useEffect(() => {
        const interval = setInterval(() => {
            const startDate = typeof start === 'string' ? parseISO(start) : new Date(start);
            const totalSeconds = differenceInSeconds(startDate, new Date());

            if (totalSeconds <= 0) {
                setCountdown({ days: '00', hours: '00', minutes: '00', seconds: '00' });
                return;
            }

            const days = Math.floor(totalSeconds / 86400);
            const hours = Math.floor((totalSeconds % 86400) / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;

            setCountdown({
                days: days.toString().padStart(2, '0'),
                hours: hours.toString().padStart(2, '0'),
                minutes: minutes.toString().padStart(2, '0'),
                seconds: seconds.toString().padStart(2, '0')
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [start]);

    if (!countdown) {
        return null;
    }

    return (
        <CdnImageBackground
            enableOverlay={false}
            imageDimensions={imageDimensions}
            setImageDimensions={setImageDimensions}
            style={Styles.imageBackground}
            uri={imageSrc.cdnImagePath}
            width={hardWidth}
        >
            <View style={{ backgroundColor: 'transparent' }}>
                <View style={Styles.wrapper}>
                    <H4
                        style={{ color: textColor || '#fff', marginBottom: layout.styleGuide.spacer2 }}
                        text={text}
                        dataSet={{ spacerBottom: `StyleGuide.Spacer2(${layout.styleGuide.spacer2})` }}
                    />
                </View>

                <View
                    style={StyleSheet.flatten(Styles.wrapper, { marginTop: layout.styleGuide.spacer2 })}
                    dataSet={{ spacerTop: `StyleGuide.Spacer2(${layout.styleGuide.spacer2})` }}
                >
                    {renderPeriod(PERIOD_TYPE.DAYS, countdown?.days)}
                    {renderPeriod(PERIOD_TYPE.HOURS, countdown?.hours)}
                    {renderPeriod(PERIOD_TYPE.MINUTES, countdown?.minutes)}
                    {renderPeriod(PERIOD_TYPE.SECONDS, countdown?.seconds)}
                </View>
                {!!actionText && onPress && (
                    <View
                        style={StyleSheet.flatten(Styles.wrapper, { marginTop: layout.styleGuide.spacer2 })}
                        dataSet={{ spacerTop: `StyleGuide.Spacer2(${layout.styleGuide.spacer2})` }}
                    >
                        <Button onPress={onPress} style={{ backgroundColor: 'red', borderWidth: 0 }} text={actionText} textStyle={{ color: textColor || '#fff' }} />
                    </View>
                )}
            </View>
        </CdnImageBackground>
    );
};

const Styles = StyleSheet.create({
    action: {
        color: '#a61a2b'
    },
    imageBackground: {
        justifyContent: 'center'
    },
    periodText: {
        color: '#fff',
        fontFamily: 'GothamBook',
        textAlign: 'center'
    },
    periodValue: {
        alignContent: 'center',
        color: '#fff',
        fontFamily: 'GothamBook',
        textAlign: 'center'
    },
    wrapper: {
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'center'
    }
});

Countdown.propTypes = {
    imageSrc: PropTypes.object.isRequired,
    actionText: PropTypes.string,
    onPress: PropTypes.func,
    start: PropTypes.string.isRequired,
    text: PropTypes.string,
    textColor: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
};
